import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { EgProblemService } from '@expertgroup/shell';
import { AuthService } from '@/app/core/auth/auth.service';
import { CopyService } from '@/app/core/copy/copy.service';
import { injectAssistantSession } from '@/app/core/auth/assistant-session';

/**
 * Opt-in: suppress the global error toast for a request (the error still propagates so the caller can
 * handle it). 401 handling is never suppressed.
 *
 * ⚠️ Opt-in is rare on purpose. Its first user was the dashboard summary, on the stated grounds that the
 * endpoint was "still being built" — a reason that expired at commit `313e7cf` while the suppression stayed,
 * turning every later 500 into a silent empty screen. Before opting a call in, say which failure the user is
 * better off not seeing.
 *
 * Today's only user: the background requests of `@expertgroup/support` (`backgroundContext`, see
 * `core/support/protax-support.ts`) — the badge poll every 60 s (with backoff), "mark as viewed" and silent list
 * refreshes. The user never asked for any of them; while support is down the user is better off with the last
 * badge count than with a toast every minute. What the user does on the support page (load, open, reply, close)
 * is a foreground request and still toasts.
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Maps API errors to Serbian toasts; re-triggers OIDC login on 401.
 *
 * A 4xx with an RFC 7807 body is ALSO reported to `EgProblemService` — the one place that remembers which
 * error is on screen after the toast is gone. The assistant reads it (red dot on the trigger, a suggested
 * question the user has to click). The toast is unchanged; the report is an addition, not a replacement.
 *
 * ⚠️ What is deliberately NOT reported:
 * - 401 — it is a login redirect (or the assistant's own outage), not a problem on the screen.
 * - 5xx and status 0 — the body carries nothing the user can act on; the story asks for 4xx only.
 * - a request the caller opted out of the toast for (`SKIP_ERROR_TOAST`) — the caller owns that failure.
 * - the assistant's own requests — a failing assistant is not an error on the ProTax screen it would explain.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(AuthService);
    const messages = inject(MessageService);
    const assistant = injectAssistantSession();
    const problems = inject(EgProblemService);
    const t = inject(CopyService).t;

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            const skipToast = req.context.get(SKIP_ERROR_TOAST);
            if (error.status === 401 && assistant?.ownsRequest(req.url)) {
                // ⚠️ Otkaz asistenta NE sme da vuče celu aplikaciju kroz ponovnu prijavu. Ovaj interceptor
                // je globalan, pa bi bez ovog razdvajanja 401 sa InternalGptService-a izbacio korisnika iz
                // ProTax-a kroz Identity — gubeći mu mesto na kom je bio i praveći da tuđi otkaz izgleda
                // kao ProTax-ov bag u prijavi.
                messages.add({ severity: 'error', summary: t('core.toast.error'), detail: t('core.error.assistantUnavailable') });
            } else if (error.status === 401) {
                auth.login();
            } else if (skipToast) {
                // caller handles it (e.g. renders an empty state)
            } else if (error.status === 0) {
                messages.add({
                    severity: 'error',
                    summary: t('core.toast.error'),
                    detail: t('core.error.serverUnreachable')
                });
            } else if (error.status >= 500) {
                messages.add({ severity: 'error', summary: t('core.toast.error'), detail: t('core.error.serverError') });
            } else {
                // Backend emits RFC 7807 problem+json (GlobalExceptionHandler): domain message is in `detail`
                // (= exception.Message for 4xx) / `title`. Legacy `message` kept as a fallback for non-problem bodies.
                //
                // ⚠️ A bare GraphQL envelope is the exception. HotChocolate rejects a document it cannot validate —
                // an operation the served schema does not know, i.e. a client ahead of its API (support before T6,
                // an Apollo query before its API slice) — with HTTP 400 and `{ errors: [...] }`. Those messages are
                // English schema diagnostics, and without this branch the toast fell through to Angular's own
                // `error.message` ("Http failure response for …"). Neither is for the user: the generic copy.
                const body = error.error as { detail?: string; title?: string; message?: string; type?: string } | null;
                const detail = isGraphqlErrorEnvelope(body) ? t('core.error.generic') : (body?.detail ?? body?.title ?? body?.message ?? error.message ?? 'Došlo je do greške.');
                messages.add({ severity: 'error', summary: t('core.toast.error'), detail });

                // ⚠️ `title` is what the toast showed, not the RFC 7807 `title`: this API's titles are the generic
                // class ("Neispravan zahtev", ApiExceptionMapping) and the assistant builds its suggested question
                // from `title` alone. The occurrence stays in `detail`, the class in `status` + `type`.
                // The toast's `??` lets an EMPTY `detail` win over a meaningful `title`; the report skips blanks
                // instead, and is not made at all when neither carries text — a question about "" explains nothing.
                const problemTitle = isProblemDetails(body) ? firstText(body.detail, body.title) : undefined;
                if (error.status >= 400 && problemTitle && !assistant?.ownsRequest(req.url)) {
                    problems.report({ source: 'server', title: problemTitle, detail: firstText(body?.detail), status: error.status, type: body?.type });
                }
            }
            return throwError(() => error);
        })
    );
};

/** The first argument that carries non-blank text, trimmed. */
function firstText(...values: (string | undefined)[]): string | undefined {
    return values.map((v) => v?.trim()).find((v) => !!v);
}

/**
 * A bare GraphQL response envelope: an object with an `errors` array and no text of its own in `detail`, `title` or
 * `message` (a body that carries those keeps the existing toast). Not a problem report either: the problem check
 * below needs `title`/`detail`, so a schema mismatch never becomes a question the assistant offers.
 */
function isGraphqlErrorEnvelope(body: unknown): boolean {
    if (!body || typeof body !== 'object') return false;
    const b = body as { errors?: unknown; detail?: unknown; title?: unknown; message?: unknown };
    return Array.isArray(b.errors) && typeof b.detail !== 'string' && typeof b.title !== 'string' && typeof b.message !== 'string';
}

/** An RFC 7807 body: an object carrying `title` or `detail` as text. A Blob, a string or `null` is not one. */
function isProblemDetails(body: unknown): body is { title?: string; detail?: string; type?: string } {
    if (!body || typeof body !== 'object') return false;
    const b = body as { title?: unknown; detail?: unknown };
    return typeof b.title === 'string' || typeof b.detail === 'string';
}
